import { styled, pxToRem } from '@withjoy/joykit/theme';
import { animationTransition } from '@shared/utils/animationTransition';
import { ParticleJoyLogo } from '@shared/components/ParticleJoyLogo';

/**
 * Container for the combined menu trigger zone (hamburger + Joy logo).
 * Hover anywhere in this zone triggers both the hamburger hover effect and particle animation.
 * `$columnGap` is a theme space-scale index so each consumer keeps its own spacing (the admin header
 * uses 4; the unified-shell nav uses 3 per Figma).
 */
export const StyledMenuTriggerZone = styled.div<{ $columnGap: number }>`
  display: flex;
  align-items: center;
  column-gap: ${({ theme, $columnGap }) => theme.space[$columnGap]};
  cursor: pointer;
`;

/**
 * Wrapper for the hamburger icon with hover/active states. The hover effect (gray background)
 * only appears around the hamburger, but is triggered when hovering anywhere in the parent
 * `StyledMenuTriggerZone`.
 */
export const StyledHamburgerWrapper = styled.div<{ $isHovered: boolean; $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.space[1]};
  border-radius: ${({ theme }) => theme.radii[2]};
  color: inherit;
  background-color: ${({ $isHovered, $isActive, theme }) => ($isActive ? theme.colors.mono3 : $isHovered ? theme.colors.mono2 : 'transparent')};
  ${({ $isHovered, theme }) => $isHovered && `color: ${theme.colors.newUIBrandPrimary};`}
  transition: ${animationTransition('background-color', 'color')};
`;

/**
 * Styled `ParticleJoyLogo`. Size + visibility are parameterised so the same trigger works for the
 * admin header (larger logo, hidden on mobile) and the unified-website-editor nav header (smaller
 * logo, always visible).
 */
export const StyledParticleJoyLogo = styled(ParticleJoyLogo)<{
  $width: number;
  $height: number;
  $alwaysVisible: boolean;
  $translateY?: number;
}>`
  display: ${({ $alwaysVisible }) => ($alwaysVisible ? 'block' : 'none')};

  ${({ theme, $alwaysVisible }) =>
    !$alwaysVisible &&
    `${theme.mediaQueries.md} {
      display: block;
    }`}

  ${({ $translateY }) => $translateY !== undefined && `transform: translateY(${pxToRem($translateY)});`}

  & > div > svg {
    width: ${({ $width }) => pxToRem($width)};
    height: ${({ $height }) => pxToRem($height)};
  }

  & canvas {
    width: ${({ $width }) => pxToRem($width)} !important;
  }
`;
